import React from 'react';
import {useSelector} from 'react-redux';

import ChooseOnMapTooltip from '../../../../components/Tooltips/ChooseOnMapTooltip.js';
import * as formInputNames from '../../../../consts/form/formInputNames.js';
import * as formItemTypes from '../../../../consts/form/formItemTypes.js';
import * as localizationKeys from '../../../../consts/localization/localizationKeys.js';
import * as modalTypes from '../../../../consts/modals/modalTypes.js';
import {getLocalizedStrings} from '../../../../services/localization/localizationService.js';
import organizationsService from '../../../../services/organizations/organizationsService.js';
import {makeSelectCurrentGateway} from '../../../../state/selectors/gateways.js';
import {makeSelectCities} from '../../../../state/selectors/organizations.js';
import helpers from '../../../../utils/helpers.js';

const getFormContent = (modalType, form) => {
    const localizedStrings = getLocalizedStrings();
    const currentGateway = useSelector(makeSelectCurrentGateway());

    const isEdit = modalType === modalTypes.EDIT_GATEWAY_MODAL;

    const title = isEdit
        ? localizedStrings[localizationKeys.GATEWAYS_PAGE_EDIT_FORM_TITLE]
        : localizedStrings[localizationKeys.GATEWAYS_PAGE_ADD_FORM_TITLE];
    const submitButton = isEdit
        ? localizedStrings[localizationKeys.GATEWAYS_PAGE_EDIT_FORM_SUBMIT_BUTTON]
        : localizedStrings[localizationKeys.GATEWAYS_PAGE_ADD_FORM_SUBMIT_BUTTON];
    const cancelButton = localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_CANCEL_BUTTON];

    const initialValues = isEdit ? currentGateway : null;

    const formFields = [
        {
            key: helpers.guid(),
            required: true,
            disabled: isEdit,
            name: formInputNames.ID,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_ID_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_ID_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_ID_FIELD_REQUIRED_MESSAGE],
        },
        {
            key: helpers.guid(),
            required: true,
            name: formInputNames.NAME,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_NAME_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_NAME_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_NAME_FIELD_REQUIRED_MESSAGE],
        },
        {
            key: helpers.guid(),
            name: formInputNames.DESCRIPTION,
            row: true,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_DESCRIPTION_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_DESCRIPTION_FIELD_PLACEHOLDER],
        },
        {
            key: helpers.guid(),
            required: true,
            formItemType: formItemTypes.SELECT,
            fieldLabel: 'value',
            name: formInputNames.CITY_ID,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_CITY_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_CITY_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_CITY_FIELD_REQUIRED_MESSAGE],
            withSearch: false,
            requestOptions: {
                selectRequestData: makeSelectCities,
                getRequestData: () => organizationsService.getCities(),
            },
            onChange: () => {
                form.setFieldsValue({
                    [formInputNames.ADDRESS]: null,
                    [formInputNames.LONGITUDE]: null,
                    [formInputNames.LATITUDE]: null,
                });
            },
        },
        {
            key: helpers.guid(),
            name: formInputNames.ADDRESS,
            required: true,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_ADDRESS_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_ADDRESS_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_ADDRESS_FIELD_REQUIRED_MESSAGE],
            suffix: <ChooseOnMapTooltip modalType={modalType} form={form} />,
        },
        {
            key: helpers.guid(),
            required: true,
            name: formInputNames.LATITUDE,
            itemDeps: formInputNames.CITY_ID,
            disabled: (_, cityID) => !cityID,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_LATITUDE_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_LATITUDE_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_LATITUDE_FIELD_REQUIRED_MESSAGE],
        },
        {
            key: helpers.guid(),
            required: true,
            name: formInputNames.LONGITUDE,
            itemDeps: formInputNames.CITY_ID,
            disabled: (_, cityID) => !cityID,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_LONGITUDE_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_LONGITUDE_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_LONGITUDE_FIELD_REQUIRED_MESSAGE],
        },
        {
            key: helpers.guid(),
            name: formInputNames.STATS_INTERVAL,
            required: true,
            row: true,
            label: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_STATS_INTERVAL_FIELD_LABEL],
            placeholder: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_STATS_INTERVAL_FIELD_PLACEHOLDER],
            errorMessage: localizedStrings[localizationKeys.GATEWAYS_PAGE_FORM_STATS_INTERVAL_FIELD_REQUIRED_MESSAGE],
            type: 'number',
            min: '1',
            onInput: (e) => {
                const value = parseInt(e.target.value, 10);

                if (value < 1) e.target.value = 1;
            },
        },
    ];

    return {formFields, title, submitButton, cancelButton, initialValues};
};

export default {
    getFormContent,
};
